import { useMemo, useState, useEffect } from "react";
import SearchInput from "./SearchInput";
import SearchResults from "./SearchResults";
import styles from "./GlobalSearch.module.css";

export default function GlobalSearch({
  hotels = [],
  searchTerm = "",
  setSearchTerm,
  onHotelSelect,
}) {
  const [activeIndex, setActiveIndex] = useState(-1);
  // Restore the previous search when returning to this page.
useEffect(() => {
  const savedQuery = sessionStorage.getItem("hotelSearchQuery");

  if (savedQuery && !searchTerm) {
    setSearchTerm(savedQuery);
  }
}, []);
  const [isOpen, setIsOpen] = useState(false);

  const query = (searchTerm || "").trim().toLowerCase();

  const results = useMemo(() => {
    if (!query) {
      return {
        states: [],
        cities: [],
        hotels: [],
      };
    }

    const normalizedHotels = hotels.map((hotel) => ({
      ...hotel,
      name: hotel.name || "",
      city: hotel.city || "",
      state: hotel.state || "",
    }));

    const states = [
      ...new Set(
        normalizedHotels
          .map((hotel) => hotel.state)
          .filter((state) => state.toLowerCase().includes(query))
      ),
    ].sort();

    const cities = [
      ...new Set(
        normalizedHotels
          .filter(
            (hotel) =>
              hotel.state.toLowerCase().includes(query) ||
              hotel.city.toLowerCase().includes(query)
          )
          .map((hotel) => hotel.city)
          .filter(Boolean)
      ),
    ].sort();

    const matchedHotels = normalizedHotels.filter(
      (hotel) =>
        hotel.name.toLowerCase().includes(query) ||
        hotel.city.toLowerCase().includes(query) ||
        hotel.state.toLowerCase().includes(query)
    );

    return {
      states,
      cities,
      hotels: matchedHotels,
    };
  }, [hotels, query]);

  // Combine all result types into one keyboard navigation sequence.
  const navigableResults = useMemo(
    () => [
      ...results.states.map((state) => ({
        type: "state",
        value: state,
      })),
      ...results.cities.map((city) => ({
        type: "city",
        value: city,
      })),
      ...results.hotels.map((hotel) => ({
        type: "hotel",
        value: hotel,
      })),
    ],
    [results]
  );

  // Reset selection when the search query changes.
  useEffect(() => {
    setActiveIndex(-1);
    setIsOpen(Boolean(query));
  }, [query]);

  const handleKeyDown = (event) => {
    if (!query || navigableResults.length === 0) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) =>
        current >= navigableResults.length - 1 ? 0 : current + 1
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) =>
        current <= 0 ? navigableResults.length - 1 : current - 1
      );
    } else if (event.key === "Enter") {
      if (!isOpen) {
        setIsOpen(true);
        return;
      }

      event.preventDefault();

      const selected = navigableResults[activeIndex];

      if (selected?.type === "hotel") {
        onHotelSelect?.(selected.value);
        setIsOpen(false);
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleResultSelect = (item) => {
    if (item.type === "hotel") {
      onHotelSelect?.(item.value);
      setIsOpen(false);
    }
  };

  return (
    <div
      className={styles.container}
      onKeyDown={handleKeyDown}
    >
  
      <SearchInput
  value={searchTerm}
  onChange={(value) => {
    setSearchTerm(value);
    sessionStorage.setItem("hotelSearchQuery", value);
    setIsOpen(Boolean(value.trim()));
  }}
/>

      {query && isOpen && (
        <SearchResults
          results={results}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onHotelSelect={onHotelSelect}
          onResultSelect={handleResultSelect}
        />
      )}
    </div>
  );
}